import { useState } from 'react';
import { Sparkles, BookOpen, GalleryHorizontal, ListOrdered, SplitSquareHorizontal, Newspaper, ShieldCheck, Mail, Search, ShoppingBag, PlayCircle, Truck } from 'lucide-react';
import { useSiteContentValue } from '@/hooks/useSiteContentValue';
import { AppShell } from '@/components/layout/AppShell';
import { SectionPageEditor } from '@/components/admin/SectionPageEditor';
import { VideoField } from '@/components/admin/VideoField';
import type { SectionDef } from '@/components/admin/SectionPageEditor';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../../nav';
import { ShopCirclesEditor } from './ShopCirclesEditor';

// Every field here maps to a real i18n key already rendered on the live
// home page (verified against the 8 actually-imported section components —
// there are 5 more files under pages/home/components that are dead code,
// never imported by page.tsx, and deliberately excluded here). Orphaned
// keys that exist in the locale bundles but aren't read by any component
// are also excluded, since editing them would have no visible effect.
const HOME_SECTIONS: SectionDef[] = [
  {
    key: 'hero',
    title: 'Hero',
    icon: Sparkles,
    images: [{ key: 'home.hero.productImage', label: 'Product Group Image' }],
    texts: [
      { key: 'home.hero.dailyTreatment', label: 'Headline — "Daily Treatment"' },
      { key: 'home.hero.prescription', label: 'Headline — "Prescription"' },
      { key: 'home.hero.kBeauty', label: 'Headline — "K-Beauty"' },
      { key: 'home.hero.subtitle', label: 'Subtitle' },
      { key: 'home.hero.ctaShopSet', label: 'Button — Shop the Set', long: true },
      { key: 'home.hero.ctaWhereToBuy', label: 'Button — Where to Buy' },
      { key: 'home.hero.product01', label: 'Product Label 1' },
      { key: 'home.hero.product02', label: 'Product Label 2' },
      { key: 'home.hero.product03', label: 'Product Label 3' },
      { key: 'home.hero.statClean', label: 'Stat — "Clean" Value' },
      { key: 'home.hero.statCleanSub', label: 'Stat — "Clean" Sub-label' },
      { key: 'home.hero.statDermaSub', label: 'Stat — Derma Sub-label' },
      { key: 'home.hero.statPrescribedSub', label: 'Stat — Prescribed Sub-label' },
      { key: 'home.hero.trustBadge1', label: 'Trust Badge 1' },
      { key: 'home.hero.trustBadge2', label: 'Trust Badge 2' },
      { key: 'home.hero.trustBadge3', label: 'Trust Badge 3' },
      { key: 'home.hero.trustBadge4', label: 'Trust Badge 4' },
      { key: 'home.hero.scroll', label: 'Scroll Hint' },
    ],
  },
  {
    key: 'brandStory',
    title: 'Brand Story',
    icon: BookOpen,
    images: [{ key: 'home.brandStory.image', label: 'Section Image' }],
    texts: [
      { key: 'home.brandStory.label', label: 'Eyebrow Label' },
      { key: 'home.brandStory.heading', label: 'Heading' },
      { key: 'home.brandStory.subheading', label: 'Subheading' },
      { key: 'home.brandStory.body', label: 'Body Text' },
      { key: 'home.brandStory.pillar1.title', label: 'Pillar 1 — Title' },
      { key: 'home.brandStory.pillar1.sub', label: 'Pillar 1 — Subtext' },
      { key: 'home.brandStory.pillar2.title', label: 'Pillar 2 — Title' },
      { key: 'home.brandStory.pillar2.sub', label: 'Pillar 2 — Subtext' },
      { key: 'home.brandStory.pillar3.title', label: 'Pillar 3 — Title' },
      { key: 'home.brandStory.pillar3.sub', label: 'Pillar 3 — Subtext' },
      { key: 'home.brandStory.cta', label: 'Button' },
    ],
  },
  {
    key: 'launchGallery',
    title: 'Launch Gallery',
    icon: GalleryHorizontal,
    images: [
      { key: 'home.launchGallery.brandPanel', label: 'Photo — Brand Panel' },
      { key: 'home.launchGallery.booth', label: 'Photo — Booth' },
      { key: 'home.launchGallery.consult', label: 'Photo — Consult' },
      { key: 'home.launchGallery.crowd', label: 'Photo — Crowd' },
      { key: 'home.launchGallery.model', label: 'Photo — Model' },
      { key: 'home.launchGallery.model2', label: 'Photo — Model 2' },
      { key: 'home.launchGallery.team', label: 'Photo — Team' },
      { key: 'home.launchGallery.team2', label: 'Photo — Team 2' },
    ],
    texts: [
      { key: 'home.launchGallery.eyebrow', label: 'Eyebrow Label' },
      { key: 'home.launchGallery.heading', label: 'Heading' },
      { key: 'home.launchGallery.subline', label: 'Subline' },
      { key: 'home.launchGallery.badge', label: 'Badge' },
      { key: 'home.launchGallery.cta', label: 'Button' },
      { key: 'home.launchGallery.caption.brandPanel', label: 'Caption — Brand Panel' },
      { key: 'home.launchGallery.caption.booth', label: 'Caption — Booth' },
      { key: 'home.launchGallery.caption.consult', label: 'Caption — Consult' },
      { key: 'home.launchGallery.caption.crowd', label: 'Caption — Crowd' },
      { key: 'home.launchGallery.caption.model', label: 'Caption — Model' },
      { key: 'home.launchGallery.caption.model2', label: 'Caption — Model 2' },
      { key: 'home.launchGallery.caption.team', label: 'Caption — Team' },
      { key: 'home.launchGallery.caption.team2', label: 'Caption — Team 2' },
    ],
  },
  {
    key: 'routine',
    title: '3-Step Routine',
    icon: ListOrdered,
    note: "The product cards themselves are managed in Inventory → Products — this is just the section's own label, heading, and button.",
    texts: [
      { key: 'home.routine.label', label: 'Eyebrow Label' },
      { key: 'home.routine.heading', label: 'Heading' },
      { key: 'home.routine.tagline', label: 'Tagline' },
      { key: 'home.routine.cta', label: 'Button' },
    ],
  },
  {
    key: 'ctaSplit',
    title: 'CTA Split',
    icon: SplitSquareHorizontal,
    images: [{ key: 'home.ctaSplit.image', label: 'Section Image' }],
    texts: [
      { key: 'home.ctaSplit.left.heading', label: 'Left Panel — Heading' },
      { key: 'home.ctaSplit.right.heading', label: 'Right Panel — Heading' },
      { key: 'home.ctaSplit.right.body', label: 'Right Panel — Body' },
      { key: 'home.ctaSplit.right.step1', label: 'Right Panel — Step 1' },
      { key: 'home.ctaSplit.right.step2', label: 'Right Panel — Step 2' },
      { key: 'home.ctaSplit.right.step3', label: 'Right Panel — Step 3' },
      { key: 'home.ctaSplit.right.ctaShop', label: 'Button — Shop' },
      { key: 'home.ctaSplit.right.ctaWhere', label: 'Button — Where to Buy' },
      // Named stat1/stat3 in code (stat2 exists in the locale bundle but
      // is never rendered) — relabeled 1/2 here since only two show.
      { key: 'home.ctaSplit.stat1.label', label: 'Stat 1 — Label' },
      { key: 'home.ctaSplit.stat1.value', label: 'Stat 1 — Value' },
      { key: 'home.ctaSplit.stat3.label', label: 'Stat 2 — Label' },
      { key: 'home.ctaSplit.stat3.value', label: 'Stat 2 — Value' },
    ],
  },
  {
    key: 'articles',
    title: 'Articles',
    icon: Newspaper,
    note: "The article cards themselves are managed in Articles — this is just the section's own label, heading, and button.",
    texts: [
      { key: 'home.articles.label', label: 'Eyebrow Label' },
      { key: 'home.articles.heading', label: 'Heading' },
      { key: 'home.articles.cta', label: 'Button' },
    ],
  },
  {
    key: 'authenticity',
    title: 'Authenticity',
    icon: ShieldCheck,
    texts: [
      { key: 'home.authenticity.label', label: 'Eyebrow Label' },
      { key: 'home.authenticity.subLabel', label: 'Sub-label' },
      { key: 'home.authenticity.heading', label: 'Heading' },
      { key: 'home.authenticity.body', label: 'Body Text' },
      { key: 'home.authenticity.cta', label: 'Button' },
      { key: 'home.authenticity.badge', label: 'Badge' },
    ],
  },
  {
    key: 'newsletter',
    title: 'Newsletter',
    icon: Mail,
    texts: [
      { key: 'home.newsletter.heading', label: 'Heading' },
      { key: 'home.newsletter.body', label: 'Body Text' },
      { key: 'home.newsletter.placeholder', label: 'Input Placeholder' },
      { key: 'home.newsletter.submit', label: 'Submit Button' },
      { key: 'home.newsletter.success', label: 'Success Message' },
      { key: 'home.newsletter.error', label: 'Error Message' },
      { key: 'home.newsletter.privacy', label: 'Privacy Note' },
    ],
  },
  {
    key: 'seo',
    title: 'SEO',
    icon: Search,
    note: 'Shown in Google search results and social media link previews — not visible on the page itself.',
    texts: [
      { key: 'seo.home.title', label: 'Meta Title', long: true },
      { key: 'seo.home.description', label: 'Meta Description', long: true },
    ],
  },
];

// The new-design Home (src/pages/home/v2/HomeV2.tsx), page key 'homeV2'.
// Product cards, prices, categories and skin types come from Inventory and
// product content; these are the section texts, images and switches.
const t = (prefix: string, keys: [string, string][]) => keys.map(([k, label]) => ({ key: `homeV2.${prefix}.${k}`, label }));
const HOME_V2_SECTIONS: SectionDef[] = [
  {
    key: 'hero',
    title: 'Banners & promo tiles',
    icon: Sparkles,
    note: 'Up to four banners, 1920×820 each. Leave a banner without an image to hide it. Banner links are site paths like /3-step-treatment or full https:// links (set them in English; they apply to every language).',
    images: [
      { key: 'home.v2.banner1', label: 'Banner 1' },
      { key: 'home.v2.banner2', label: 'Banner 2' },
      { key: 'home.v2.banner3', label: 'Banner 3' },
      { key: 'home.v2.banner4', label: 'Banner 4' },
      { key: 'home.v2.tile1', label: 'Promo tile 1' },
      { key: 'home.v2.tile2', label: 'Promo tile 2' },
    ],
    texts: [
      ...t('hero', [
        ['banner1Link', 'Banner 1 — link'],
        ['banner2Link', 'Banner 2 — link'],
        ['banner3Link', 'Banner 3 — link'],
        ['banner4Link', 'Banner 4 — link'],
        ['tile1Label', 'Tile 1 — small label'],
        ['tile1Title', 'Tile 1 — title ({{amount}} = set saving)'],
        ['tile2Label', 'Tile 2 — small label'],
        ['tile2Title', 'Tile 2 — title'],
      ]),
    ],
  },
  {
    key: 'shop',
    title: 'Shop',
    icon: ShoppingBag,
    note: 'Products and prices come from Inventory → Products. The category and concern circles (photos, names, which products) are edited in the “Shop categories & concerns” tab above.',
    texts: t('shop', [
      ['byCategory', 'Heading — by category'],
      ['byConcern', 'Heading — by concern'],
      ['all', 'Circle — All'],
      ['allProducts', 'Sidebar — All products'],
      ['categoryHeading', 'Sidebar heading'],
      ['product', 'Count — product'],
      ['products', 'Count — products'],
      ['sortLabel', 'Sort label'],
      ['sortNew', 'Sort — Newest'],
      ['sortFeatured', 'Sort — Featured'],
      ['sortLow', 'Sort — low to high'],
      ['sortHigh', 'Sort — high to low'],
      ['sale', 'Sale badge'],
      ['empty', 'No results text'],
      ['showAll', 'Show all button'],
      ['showMore', 'Show more button'],
    ]).concat(
      t('sbar', [
        ['giftBox', 'Bottom purchase bar — line under the set name'],
        ['save', 'Bottom purchase bar — "Save"'],
        ['add', 'Bottom purchase bar — button'],
      ]),
    ),
  },
  {
    key: 'ritual',
    title: 'Daily ritual',
    icon: ListOrdered,
    texts: t('ritual', [
      ['label', 'Eyebrow label'],
      ['heading', 'Heading'],
      ['headingEm', 'Heading — second line'],
      ['cta', 'Button'],
    ]),
  },
  {
    key: 'watch',
    title: 'Watch & shop videos',
    icon: PlayCircle,
    images: [
      { key: 'home.v2.watch.full', label: 'Cover — full routine' },
      { key: 'home.v2.watch.booster', label: 'Cover — Step 01' },
      { key: 'home.v2.watch.serum', label: 'Cover — Step 02' },
      { key: 'home.v2.watch.cream', label: 'Cover — Step 03' },
    ],
    extra: (
      <div className="grid gap-3 sm:grid-cols-2">
        <VideoField
          contentKey="homeV2.watch.video.full"
          label="Video — full routine"
          hint="Opens when the first card is tapped. MP4 under 50 MB, or an .mp4 link."
          youtube={false}
        />
        <VideoField
          contentKey="homeV2.watch.video.booster"
          label="Video — Step 01 (Booster)"
          hint="Plays on the card and opens when tapped. Empty = the built-in clip."
          youtube={false}
        />
        <VideoField
          contentKey="homeV2.watch.video.serum"
          label="Video — Step 02 (Serum)"
          hint="Plays on the card and opens when tapped. Empty = the built-in clip."
          youtube={false}
        />
        <VideoField
          contentKey="homeV2.watch.video.cream"
          label="Video — Step 03 (Cream)"
          hint="Plays on the card and opens when tapped. Empty = the built-in clip."
          youtube={false}
        />
      </div>
    ),
    texts: t('watch', [
      ['label', 'Eyebrow label'],
      ['heading', 'Heading'],
      ['headingEm', 'Heading — highlighted part'],
      ['full', 'Card tag — full routine'],
      ['step', 'Card tag — Step'],
      ['shopNow', 'Video button — Shop now'],
    ]),
  },
  {
    key: 'brand',
    title: 'Brand story',
    icon: BookOpen,
    images: [
      { key: 'home.v2.brand1', label: 'Large photo' },
      { key: 'home.v2.brand2', label: 'Small photo' },
    ],
    texts: t('brand', [
      ['kicker', 'Eyebrow label'],
      ['heading', 'Heading'],
      ['headingEm', 'Heading — second line'],
      ['body', 'Body (after “Rx.K Seoul”)'],
      ['fact1Title', 'Fact 1 — title'],
      ['fact1Body', 'Fact 1 — text'],
      ['fact2Title', 'Fact 2 — title'],
      ['fact2Body', 'Fact 2 — text'],
      ['fact3Title', 'Fact 3 — title'],
      ['fact3Body', 'Fact 3 — text'],
      ['fact4Title', 'Fact 4 — title'],
      ['fact4Body', 'Fact 4 — text'],
      ['ctaStory', 'Button — story'],
      ['ctaWhere', 'Button — where to buy'],
    ]),
  },
  {
    key: 'launch',
    title: 'K-Goods Festa gallery',
    icon: GalleryHorizontal,
    images: [
      { key: 'home.launchGallery.crowd', label: 'Photo 1 (large)' },
      { key: 'home.launchGallery.model', label: 'Photo 2' },
      { key: 'home.launchGallery.consult', label: 'Photo 3' },
      { key: 'home.launchGallery.booth', label: 'Photo 4' },
      { key: 'home.launchGallery.brandPanel', label: 'Photo 5' },
    ],
    texts: t('launch', [
      ['label', 'Eyebrow label'],
      ['heading', 'Heading'],
      ['headingEm', 'Heading — highlighted part'],
      ['subtitle', 'Subtitle'],
      ['cap1', 'Caption 1'],
      ['cap2', 'Caption 2'],
      ['cap3', 'Caption 3'],
      ['cap4', 'Caption 4'],
      ['cap5', 'Caption 5'],
    ]),
  },
  {
    key: 'verify',
    title: 'Verify (hologram)',
    icon: ShieldCheck,
    images: [{ key: 'home.v2.hologram', label: 'Hologram sticker' }],
    texts: t('verify', [
      ['kicker', 'Eyebrow label'],
      ['heading', 'Heading'],
      ['headingEm', 'Heading — second line'],
      ['body', 'Body'],
      ['step1', 'Step 1'],
      ['step1Note', 'Step 1 — note'],
      ['step2', 'Step 2'],
      ['step2Note', 'Step 2 — note'],
      ['step3', 'Step 3'],
      ['step3Note', 'Step 3 — note'],
      ['cta', 'Button'],
      ['note', 'Small note'],
      ['guide', 'Video button'],
      ['holoTitle', 'Hologram — title'],
      ['holoNote', 'Hologram — note'],
    ]),
  },
  {
    key: 'services',
    title: 'Service strip',
    icon: Truck,
    texts: t('services', [
      ['s1Title', '1 — title'],
      ['s1Body', '1 — text'],
      ['s2Title', '2 — title'],
      ['s2Body', '2 — text'],
      ['s3Title', '3 — title'],
      ['s3Body', '3 — text'],
      ['s4Title', '4 — title'],
      ['s4Body', '4 — text'],
    ]),
  },
];

export default function AdminHomePagePage() {
  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <HomeEditors />
    </AppShell>
  );
}

// Opens on whichever design is live (Settings → Design); both stay editable.
function HomeEditors() {
  const design = useSiteContentValue('site.design', 'en', 'classic');
  const [tab, setTab] = useState<'new' | 'shop' | 'classic' | null>(null);
  const active = tab ?? (design === 'classic' ? 'classic' : 'new');
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1.5">
        {(
          [
            ['new', 'New design'],
            ['shop', 'Shop categories & concerns'],
            ['classic', 'Classic design'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`text-[12.5px] font-semibold px-3.5 py-2 rounded-full cursor-pointer transition-colors ${active === key ? 'bg-primary-800 text-white' : 'bg-surface-muted text-ink-faint hover:text-ink'}`}
          >
            {label}
          </button>
        ))}
      </div>
      {active === 'new' ? (
        <SectionPageEditor key="homeV2" pageKey="homeV2" title="Home Page — new design" sections={HOME_V2_SECTIONS} />
      ) : active === 'shop' ? (
        <ShopCirclesEditor />
      ) : (
        <SectionPageEditor key="home" pageKey="home" title="Home Page — classic design" sections={HOME_SECTIONS} />
      )}
    </div>
  );
}
